import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useNavigate} from 'react-router-dom'
import {useFormik} from 'formik'
import {requestPassword} from '../core/_requests'
import {useIntl} from 'react-intl'
import {useLang} from '../../../../_metronic/i18n/Metronici18n'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {brandLogo} from '../../../tenant/applyBranding'
import {createResetTimer, resetTimerQuery} from '../resetTimer'

const initialValues = {
  email: '',
}

export function ForgotPassword() {
  const {formatMessage} = useIntl()
  const lang = useLang()
  const [loading, setLoading] = useState(false)

  const forgotPasswordSchema = Yup.object().shape({
    email: Yup.string()
      .email(formatMessage({id: 'VALIDATION.EMAIL.FORMAT'}))
      .min(3, formatMessage({id: 'VALIDATION.MIN_LENGTH'}, {length: 3}))
      .max(50, formatMessage({id: 'VALIDATION.MAX_LENGTH'}, {length: 50}))
      .required(formatMessage({id: 'VALIDATION.REQUIRED_FIELD'})),
  })
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const navigate = useNavigate()
  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setHasErrors(undefined)
      try {
        const response = await requestPassword(values.email, lang)
        const timer = createResetTimer(response.data.expires_in)
        setHasErrors(false)
        setLoading(false)
        setSubmitting(false)
        navigate(
          `/auth/verify-otp?email=${encodeURIComponent(values.email)}${resetTimerQuery(timer)}`
        )
      } catch (error: any) {
        setHasErrors(true)
        setStatus(error.response?.data?.message || formatMessage({id: 'failed_to_send_code'}))
        setLoading(false)
        setSubmitting(false)
      }
    },
  })

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_login_password_reset_form'
      onSubmit={formik.handleSubmit}
    >
      <div>
        <img
          alt=''
          width='170'
          className='mb-10 mb-10 mx-auto d-block'
          src={`${brandLogo(
            lang === 'ar',
            toAbsoluteUrl('/media/logos/logo_revamp' + (lang === 'ar' ? '-ar' : '') + '.svg')
          )}`}
        />
      </div>

      <div className='text-center mb-10'>
        <h1 className='text-dark fw-bolder mb-3'>{formatMessage({id: 'AUTH.FORGOT.TITLE'})}</h1>
        <div className='text-gray-500 fw-semibold fs-6'>
          {formatMessage({id: 'AUTH.FORGOT.DESC'})}
        </div>
      </div>

      {hasErrors === true && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      )}

      {hasErrors === false && (
        <div className='mb-10 bg-light-info p-8 rounded'>
          <div className='text-info'>{formatMessage({id: 'code_sent_check_email'})}</div>
        </div>
      )}

      <div className='fv-row mb-8'>
        <label className='form-label fw-bolder text-gray-900 fs-6'>
          {formatMessage({id: 'AUTH.INPUT.EMAIL'})}
        </label>
        <input
          type='email'
          placeholder=''
          autoComplete='off'
          dir='ltr'
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control bg-transparent',
            {'is-invalid': formik.touched.email && formik.errors.email},
            {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
          style={{textAlign: 'left'}}
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.email}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
        <button
          type='submit'
          id='kt_password_reset_submit'
          className='btn btn-primary me-4'
          disabled={loading || formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className='indicator-label'>{formatMessage({id: 'submit'})}</span>}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              {formatMessage({id: 'please_wait'})}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        <Link to='/auth/login'>
          <button
            type='button'
            id='kt_login_password_reset_form_cancel_button'
            className='btn btn-light'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {formatMessage({id: 'cancel'})}
          </button>
        </Link>{' '}
      </div>
      {/* end::Form group */}
    </form>
  )
}
